/**
 * weblabTrackerService — GraphQL access for the Weblab Tracker plugin.
 *
 * Wraps the `getWeblabView` query via AWS Amplify's generateClient(),
 * consistent with dashboardService.ts. The query is backed by the existing
 * weblab_athena_client.py Lambda.
 */

import { generateClient } from "aws-amplify/api";
import { getWeblabView as getWeblabViewQuery } from "../../../../graphql/queries";
import type { WeblabViewInput } from "../../../../models/API";

const client = generateClient();

export interface WeblabViewResult {
  columns: { name: string; type: string }[];
  rows: Record<string, any>[];
  rowCount?: number;
}

/**
 * Extract a human-readable message from whatever Amplify / AppSync threw.
 *
 * Amplify's client.graphql() rejects with an object shaped
 * `{ errors: [{ message }], ... }` (NOT an Error with `.message`), so a naive
 * `err.message` read yields undefined and the real cause is lost. This digs
 * the actual message out of every shape we've seen.
 */
export function extractGraphQLError(err: any): string {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  // Amplify GraphQL error shape: { errors: [{ message }] }
  if (Array.isArray(err.errors) && err.errors.length > 0) {
    return err.errors.map((e: any) => e?.message ?? String(e)).join("; ");
  }
  if (err.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * Execute the weblab view query against Athena via AppSync.
 *
 * The Lambda returns { statusCode, message, data: { columns, rows } }.
 * `data` may arrive as an AWSJSON string, so we parse defensively.
 *
 * Note: client.graphql() is cast to `any` before reading `.data` — the
 * Amplify GraphQLResult union otherwise triggers TS2339 on `.data`.
 */
export async function fetchWeblabView(
  input: WeblabViewInput
): Promise<WeblabViewResult> {
  let result: any;
  try {
    result = (await client.graphql({
      query: getWeblabViewQuery,
      variables: { input },
    })) as any;
  } catch (err) {
    // Surface the real AppSync/Lambda error (timeout, payload size, SQL error)
    throw new Error(extractGraphQLError(err));
  }

  const response = result.data?.getWeblabView;
  if (!response || response.statusCode !== 200) {
    throw new Error(response?.message ?? "Failed to fetch weblab view");
  }
  let payload: any = response.data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = null;
    }
  }
  const columns = Array.isArray(payload?.columns) ? payload.columns : [];
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  return {
    columns,
    rows,
    rowCount:
      typeof payload?.rowCount === "number" ? payload.rowCount : rows.length,
  };
}

const WEBLAB_DATABASE = "weblab_tableau_prod";
const WEBLAB_TABLE = "weblab_metrics_data_prod";

/**
 * Fetch the distinct list of team names with launched weblabs in the latest
 * snapshot.
 *
 * IMPORTANT: these are the WEBLAB TRACKER's team names (the export DAG stamps
 * each row's team_name from Weblab's own getTeams), NOT FINN's teams. The
 * dropdown must be sourced from here so its values match the team_name column
 * exactly — querying FINN's getTeams would produce non-matching names and
 * filter to zero rows.
 */
export async function fetchWeblabTeams(): Promise<string[]> {
  const query = `
    SELECT DISTINCT team_name
    FROM ${WEBLAB_TABLE}
    WHERE run_date = (SELECT max(run_date) FROM ${WEBLAB_TABLE})
      AND LOWER(TRIM(status)) = 'launched'
      AND team_name IS NOT NULL
    ORDER BY team_name
  `.trim();

  const result = await fetchWeblabView({
    query,
    database: WEBLAB_DATABASE,
    max_results: 1000,
  });

  return result.rows
    .map((r) => r.team_name)
    .filter((t): t is string => typeof t === "string" && t.trim() !== "");
}

/**
 * Fetch the full column set for a SINGLE weblab row, by its unique `id`.
 *
 * WHY: the all-teams list query must stay lean to fit under the Lambda 6MB
 * response cap, so the grid carries only ~lean columns. The detail panel needs
 * every field, but only for one weblab at a time — so we lazy-load the full
 * row on row click. Payload is one row, well under any limit.
 *
 * `id` (not weblab_id) is the unique key — the same weblab_id can appear on
 * multiple rows (different treatments/marketplaces), each with a distinct id.
 */
export async function fetchWeblabDetailById(
  id: string | number
): Promise<Record<string, any> | null> {
  // `id` is a bigint column in Athena — do NOT quote it.
  // Validate it's numeric to prevent injection.
  const numId = Number(id);
  if (isNaN(numId)) return null;
  const idLiteral = String(numId);
  const query = `
    SELECT *
    FROM ${WEBLAB_TABLE}
    WHERE run_date = (SELECT max(run_date) FROM ${WEBLAB_TABLE})
      AND id = ${idLiteral}
    LIMIT 1
  `.trim();

  const result = await fetchWeblabView({
    query,
    database: WEBLAB_DATABASE,
    max_results: 1,
  });

  return result.rows.length > 0 ? result.rows[0] : null;
}
